import React, { useEffect, useState, useRef, useCallback } from 'react';
import { getBanners } from '../services/bannerService';

const BannerSlider = () => {
  const [banners, setBanners] = useState([]);
  const [current, setCurrent] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const autoPlayRef = useRef(null);

  useEffect(() => {
    getBanners().then(res => {
      setBanners(res.data.filter(b => b.isActive));
    });
  }, []);

  const goTo = useCallback((next) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrent(next);
    setTimeout(() => setIsTransitioning(false), 500);
  }, [isTransitioning]);

  useEffect(() => {
    if (banners.length > 1 && !isHovered && !isDragging) {
      autoPlayRef.current = setInterval(() => {
        setCurrent(prev => {
          const next = (prev + 1) % banners.length;
          return next;
        });
      }, 5000);
    }
    return () => clearInterval(autoPlayRef.current);
  }, [banners, isHovered, isDragging]);

  const handleDragStart = (e) => {
    setIsDragging(true);
    setDragStart(e.type === 'touchstart' ? e.touches[0].clientX : e.clientX);
  };

  const handleDragMove = (e) => {
    if (!isDragging) return;
    const x = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX;
    setDragOffset(x - dragStart);
  };

  const handleDragEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);
    if (dragOffset < -50) goTo((current + 1) % banners.length);
    else if (dragOffset > 50) goTo((current - 1 + banners.length) % banners.length);
    setDragOffset(0);
  };

  if (!banners.length) return null;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:opsz,wght@14..32,300;14..32,400;14..32,500;14..32,600;14..32,700;14..32,800&display=swap');

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body, html {
          overflow-x: hidden;
          width: 100%;
        }

        .fullwidth-slider-wrapper {
          width: 100vw;
          position: relative;
          left: 50%;
          right: 50%;
          margin-left: -50vw;
          margin-right: -50vw;
          padding: 7px;
          // border-radius: 18px;
          // background: linear-gradient(135deg, #0f0f1a 0%, #1a1a2e 100%);
        }

        .fullwidth-slider-root {
          position: relative;
          width: 100%;
          max-width: 100%;
          padding: 10px 0 10px;
          font-family: 'Inter', sans-serif;
          overflow: hidden;
          background: transparent;
        }

        .fullwidth-viewport {
          position: relative;
          width: 100%;
          overflow: hidden;
        }

        .fullwidth-strip {
          display: flex;
          will-change: transform;
          cursor: grab;
          user-select: none;
        }
        .fullwidth-strip:active { cursor: grabbing; }

        .fullwidth-slide {
          flex: 0 0 100%;
          aspect-ratio: 16/9;
          overflow: hidden;
          position: relative;
          border-radius: 15px;
        }

        .fullwidth-slide img {
          width: 100%;
          height: 100%;
          object-fit: contain;
          object-position: center;
          display: block;
          pointer-events: none;
          -webkit-user-drag: none;
          background: #0f0f1a;
        }

        /* Navigation Arrows */
        .fullwidth-arrow {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          z-index: 20;
          border: none;
          cursor: pointer;
          width: 48px;
          height: 48px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255,255,255,0.95);
          backdrop-filter: blur(12px);
          box-shadow: 0 8px 20px rgba(0,0,0,0.15);
          transition: all 0.3s cubic-bezier(0.2, 0.9, 0.4, 1.1);
          outline: none;
          padding: 0;
          color: #1e293b;
        }

        .fullwidth-arrow:hover {
          background: white;
          transform: translateY(-50%) scale(1.1);
          box-shadow: 0 12px 28px rgba(0,0,0,0.25);
          color: #6366f1;
        }

        .fullwidth-arrow:active { transform: translateY(-50%) scale(0.96); }

        .fullwidth-arrow.left { left: 20px; }
        .fullwidth-arrow.right { right: 20px; }

        .fullwidth-arrow svg {
          width: 22px;
          height: 22px;
          stroke: currentColor;
          stroke-width: 2.5;
          fill: none;
          stroke-linecap: round;
          stroke-linejoin: round;
        }

        /* Dots Navigation */
        .fullwidth-dots {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 10px;
          margin-top: 24px;
          flex-wrap: wrap;
          padding: 0 16px;
        }

        .fullwidth-dot {
          border: none;
          border-radius: 50%;
          height: 8px;
          width: 8px;
          padding: 0;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          outline: none;
          background: rgba(255, 255, 255, 0.4);
        }

        .fullwidth-dot.active {
          width: 28px;
          border-radius: 4px;
          background: linear-gradient(135deg, #6366f1, #8b5cf6);
          box-shadow: 0 2px 8px rgba(99, 102, 241, 0.5);
        }

        .fullwidth-dot:hover:not(.active) { 
          background: rgba(99, 102, 241, 0.7); 
          transform: scale(1.2);
        }

        /* Progress Bar */
        .fullwidth-progress {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: rgba(255,255,255,0.2);
          z-index: 15;
          pointer-events: none;
        }

        .fullwidth-progress-fill {
          height: 100%;
          background: linear-gradient(90deg, #6366f1, #a855f7, #ec4899);
          width: 0%;
          transition: width 0.1s linear;
          box-shadow: 0 0 8px rgba(99, 102, 241, 0.5);
        }

        /* Slide Counter */
        .fullwidth-counter {
          position: absolute;
          bottom: 20px;
          right: 20px;
          z-index: 15;
          background: rgba(0,0,0,0.7);
          backdrop-filter: blur(12px);
          padding: 6px 14px;
          border-radius: 40px;
          font-size: 13px;
          font-weight: 500;
          color: white;
          letter-spacing: 0.5px;
          font-family: 'Inter', monospace;
          pointer-events: none;
          border: 1px solid rgba(255,255,255,0.2);
        }

        /* Hide arrows on very small screens for better UX */
        @media (max-width: 768px) {
          .fullwidth-arrow {
            width: 40px;
            height: 40px;
          }
          .fullwidth-arrow svg {
            width: 18px;
            height: 18px;
          }
          .fullwidth-arrow.left { left: 12px; }
          .fullwidth-arrow.right { right: 12px; }
          .fullwidth-counter {
            bottom: 16px;
            right: 16px;
            padding: 4px 10px;
            font-size: 11px;
          }
        }

        @media (max-width: 640px) {
          .fullwidth-arrow {
            width: 36px;
            height: 36px;
            background: rgba(255,255,255,0.9);
          }
          .fullwidth-dots {
            gap: 8px;
            margin-top: 20px;
          }
          .fullwidth-dot {
            height: 6px;
            width: 6px;
          }
          .fullwidth-dot.active {
            width: 24px;
          }
        }

        @media (max-width: 480px) {
          .fullwidth-arrow {
            width: 32px;
            height: 32px;
          }
          .fullwidth-arrow svg {
            width: 16px;
            height: 16px;
          }
          .fullwidth-counter {
            bottom: 12px;
            right: 12px;
            padding: 3px 8px;
            font-size: 10px;
          }
          .fullwidth-dots {
            gap: 6px;
            margin-top: 16px;
          }
        }

        /* Touch device optimizations */
        @media (hover: hover) {
          .fullwidth-arrow {
            opacity: 0;
          }
          .fullwidth-slider-root:hover .fullwidth-arrow {
            opacity: 1;
          }
        }

        @media (hover: none) {
          .fullwidth-arrow {
            opacity: 0.8;
          }
        }

        /* Animation for slides */
        .fullwidth-slide {
          animation: fadeSlide 0.5s ease-out;
        }

        @keyframes fadeSlide {
          from {
            opacity: 0.7;
            transform: scale(0.98);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>

      <div className="fullwidth-slider-wrapper">
        <div
          className="fullwidth-slider-root"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => { setIsHovered(false); handleDragEnd(); }}
        >
          <div className="fullwidth-viewport">
            <div
              className="fullwidth-strip"
              style={{
                transform: `translateX(calc(-${current} * 100% + ${dragOffset}px))`,
                transition: isDragging ? 'none' : 'transform 0.5s cubic-bezier(0.2, 0.9, 0.4, 1.1)',
              }}
              onMouseDown={handleDragStart}
              onMouseMove={handleDragMove}
              onMouseUp={handleDragEnd}
              onTouchStart={handleDragStart}
              onTouchMove={handleDragMove}
              onTouchEnd={handleDragEnd}
            >
              {banners.map((banner, index) => (
                <div
                  key={banner.id ?? index}
                  className="fullwidth-slide"
                >
                  <img
                    src={banner.image}
                    alt={`Banner ${index + 1}`}
                    draggable={false}
                    loading={index === current ? 'eager' : 'lazy'}
                  />
                </div>
              ))}
            </div>

            {/* Progress Bar */}
            {/* {!isHovered && banners.length > 1 && (
              <div className="fullwidth-progress">
                <div 
                  key={`progress-${current}`}
                  className="fullwidth-progress-fill"
                  style={{
                    width: '100%',
                    animation: `progressAnimation 5s linear forwards`
                  }}
                />
              </div>
            )} */}

            {/* Slide Counter */}
            {/* <div className="fullwidth-counter">
              {String(current + 1).padStart(2, '0')} / {String(banners.length).padStart(2, '0')}
            </div> */}

            {/* Navigation Arrows */}
            {/* {banners.length > 1 && (
              <>
                <button
                  className="fullwidth-arrow left"
                  onClick={e => { e.stopPropagation(); goTo((current - 1 + banners.length) % banners.length); }}
                  aria-label="Previous slide"
                >
                  <svg viewBox="0 0 24 24">
                    <polyline points="15 18 9 12 15 6" />
                  </svg>
                </button>
                <button
                  className="fullwidth-arrow right"
                  onClick={e => { e.stopPropagation(); goTo((current + 1) % banners.length); }}
                  aria-label="Next slide"
                >
                  <svg viewBox="0 0 24 24">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>
              </>
            )} */}
          </div>

          {/* Dots Navigation */}
          {/* {banners.length > 1 && (
            <div className="fullwidth-dots">
              {banners.map((_, idx) => (
                <button
                  key={idx}
                  className={`fullwidth-dot ${idx === current ? 'active' : ''}`}
                  onClick={() => goTo(idx)}
                  aria-label={`Go to slide ${idx + 1}`}
                />
              ))}
            </div>
          )} */}
        </div>
      </div>

      <style>{`
        @keyframes progressAnimation {
          from {
            width: 0%;
          }
          to {
            width: 100%;
          }
        }
      `}</style>
    </>
  );
};

export default BannerSlider;